/**
 * Admin CRUD + auth schemas for `users` table.
 * Source of truth: docs/architecture/database-schema.md §4.1 (users + user_role).
 *
 * Story 1.7 expands this file with authentication payloads (login + me) and
 * password-policy enforced schemas (`UserCreate`, `UserPasswordUpdate`).
 *
 * **Password policy (Story 1.7 §AC #10.1):**
 *   - >= 12 characters
 *   - >= 1 uppercase, >= 1 lowercase, >= 1 digit, >= 1 special
 *     (`!@#$%^&*()_+-=[]{}|;:,.<>?`)
 *
 * The TypeBox `pattern` regex below is a *fast* shape gate — the canonical,
 * human-readable enforcement happens server-side in
 * `apps/api/src/services/auth/password-policy.ts` so that 400 responses can
 * surface a precise reason (`WEAK_PASSWORD`).
 */
import { Type } from '@sinclair/typebox';
import { UserRole } from '@sdr-agend/schemas';
/**
 * Strong password regex — mirrors `validatePassword` rules.
 *
 * Lookaheads enforce each character class; final `.{12,200}` enforces length
 * and the implicit ASCII-printable boundary (TypeBox passes the value as a
 * string, so we accept any printable). The dedicated server-side helper is
 * still the source of truth.
 */
export const STRONG_PASSWORD_PATTERN = '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=\\[\\]{}|;:,.<>?]).{12,200}$';
/**
 * `User` — full read-side projection (no password fields exposed).
 */
export const User = Type.Object({
    id: Type.String({ format: 'uuid' }),
    email: Type.String({ format: 'email' }),
    name: Type.String({ minLength: 1 }),
    role: UserRole,
    active: Type.Boolean(),
    // 2026-07-21 (user request) — links a `role='vendedor'` login to the
    // matching `sellers.ghl_user_id`. Null for every other role.
    ghlUserId: Type.Union([Type.String(), Type.Null()]),
    lastLoginAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
});
/**
 * `UserCreate` — payload for `POST /admin/users` (admin-only).
 *
 * Password is intentionally accepted as a plain string at the schema layer
 * (length 1..200) so the route handler can run `validatePassword()` and
 * surface a precise `WEAK_PASSWORD` 400 with a Portuguese reason. TypeBox
 * `pattern` would short-circuit with a generic shape error which the story
 * spec (AC #10.1) does NOT want.
 *
 * `name`/`phone` are optional here (2026-07-21, user request) because a
 * `role='vendedor'` creation supplies `sellerId` instead — the route derives
 * name/phone/ghlUserId from the existing `sellers` row rather than having the
 * admin retype data that's already on file. The route handler enforces "name
 * required unless sellerId is present" for a precise error, same rationale as
 * the password-policy split above (fast TypeBox shape gate would only give a
 * generic 400).
 */
export const UserCreate = Type.Object({
    email: Type.String({ format: 'email' }),
    password: Type.String({ minLength: 1, maxLength: 200 }),
    name: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
    role: Type.Optional(UserRole),
    active: Type.Optional(Type.Boolean()),
    /**
     * When creating a `role='vendedor'` login, pass the existing `sellers.id`
     * instead of retyping name/phone/GHL id — the route loads that seller and
     * derives all three (rejecting if the seller has no `ghl_user_id` on file,
     * since a vendedor login MUST be linkable).
     */
    sellerId: Type.Optional(Type.String({ format: 'uuid' })),
});
/**
 * `UserUpdate` — payload for `PATCH /admin/users/:id` (admin-only).
 *
 * `email` is intentionally absent: changing email requires a manual SQL
 * audit-trail step (Story 1.7 §AC #7). `password` is optional; when present
 * the same policy applies (enforced in the route handler — see `UserCreate`).
 */
export const UserUpdate = Type.Object({
    name: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
    role: Type.Optional(UserRole),
    active: Type.Optional(Type.Boolean()),
    password: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
});
/**
 * `UserPasswordUpdate` — dedicated alias used when only the password field
 * is changing (kept for clarity / future password-only endpoints).
 */
export const UserPasswordUpdate = Type.Object({
    password: Type.String({ minLength: 1, maxLength: 200 }),
});
/**
 * `UserLoginRequest` — body of `POST /admin/auth/login`.
 *
 * Note: NO password-policy enforcement here. We must accept any string the
 * user types so the response is consistent (anti-enumeration); the server
 * compares against the bcrypt hash regardless.
 */
export const UserLoginRequest = Type.Object({
    email: Type.String({ format: 'email' }),
    password: Type.String({ minLength: 1, maxLength: 200 }),
});
/**
 * Backwards-compat alias — `UserLogin` is the historical name (Story 1.6).
 */
export const UserLogin = UserLoginRequest;
/**
 * `UserLoginResponse` — successful login envelope.
 *
 * Contains the JWT (HS256, 8h) plus the same projection used by `GET
 * /admin/auth/me`. Frontend stores `token` in Zustand and persists to
 * localStorage (MVP trade-off documented in Story 1.7 §Riscos).
 */
export const UserLoginResponse = Type.Object({
    token: Type.String({ minLength: 1 }),
    user: User,
});
/**
 * `UserMeResponse` — body of `GET /admin/auth/me`.
 */
export const UserMeResponse = Type.Object({
    user: User,
});
/**
 * `UserListResponse` — body of `GET /admin/users` (admin-only).
 */
export const UserListResponse = Type.Object({
    users: Type.Array(User),
});
/**
 * Generic admin error envelope used by all `/admin/*` routes.
 */
export const AdminError = Type.Object({
    error: Type.Object({
        code: Type.String(),
        message: Type.Optional(Type.String()),
    }),
});
//# sourceMappingURL=users.js.map